import { Dialog, Button, Switcher, Spinner } from "components/ui";
import { BaseModal, ConfirmDialog } from "components/shared";
import { STORE_HUB } from "components/ui/utils/constant";
import React, { Suspense, useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { toggleTicketDialog } from "../store/stateSlice";
import AddTicket from "./AddTicket";
import TicketContent from "../components/TicketContent";
import { resetValuesForTicket } from "../store/globalSlice";
import { deleteKey } from "utils/storeHube/localStorage";
import { isMobile } from "utils/storeHube/helpers";
import usePermissions from "utils/hooks/usePermissions";
import { isNewInboxTenant } from "utils/tenant";
import { HiOutlineClock } from "react-icons/hi";

// El modal de detalle de hallazgo se monta GLOBAL en el layout (uno solo para
// toda la app). Como el store de tickets es compartido entre la vista nueva y
// la legacy, la tabla legacy abre este mismo modal. Para que los tenants
// legacy vean su detalle viejo (no el rediseno) cargamos su TicketContent
// snapshot bajo demanda. Crear hallazgo (AddTicket) sigue siendo el nuevo para
// todos (decision de la bifurcacion): solo se bifurca el detalle.
const LegacyTicketContent = React.lazy(() =>
    import("views/tickets-legacy/components/TicketContent")
);


const TicketDialog = () => {

    const { t } = useTranslation()
    const isMobileDevice = isMobile()
    const dispatch = useDispatch()
    const viewTicket = useSelector(state => state.ticketList.state.viewTicket)
    const selectedTicket = useSelector(state => state.ticketList.data.selectedTicket)
    const dialogView = useSelector(state => state.ticketList.state.dialogView)
    const QUESTION_PRIMARY_KEY = `question_group_primary`
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showTicketScheduleForm, setShowTicketScheduleForm] = useState(false)
    const [isResponsibleSelected, setIsResponsibleSelected] = useState(false)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false)
    const { checkPermission } = usePermissions()
    const canScheduleTickets = checkPermission("ticket_schedule.can_store")

    // Dismiss schedule form when permissions change
    useEffect(() => {
        if (!canScheduleTickets && showTicketScheduleForm) {
            setShowTicketScheduleForm(false)
        }
    }, [canScheduleTickets, showTicketScheduleForm])

    // Reset all transient state when the dialog closes
    useEffect(() => {
        if (!viewTicket) {
            setShowTicketScheduleForm(false)
            setHasUnsavedChanges(false)
            setShowUnsavedConfirm(false)
            setIsResponsibleSelected(false)
        }
    }, [viewTicket])

    // Guard against the one-render gap between state change and effect cleanup
    const effectiveShowScheduleForm = showTicketScheduleForm && canScheduleTickets

    const ticketAlias = t('ticket_alias')
    const ticketAliasLower = typeof ticketAlias === 'string' ? ticketAlias.toLowerCase() : ticketAlias
    const createLabel = `${t('create')} ${ticketAliasLower}`
    const createLoadingLabel = `${t('creating', { defaultValue: 'Creando' })} ${ticketAliasLower}`

    const modalTitle = `${t('create')} ${ticketAlias}`

    const handleDirtyChange = useCallback((isDirty) => {
        setHasUnsavedChanges(isDirty)
    }, [])

    const handleForceClose = useCallback(() => {
        dispatch(toggleTicketDialog(false))
        dispatch(resetValuesForTicket())
        deleteKey(QUESTION_PRIMARY_KEY)
        setShowTicketScheduleForm(false)
        setIsResponsibleSelected(false)
        setShowUnsavedConfirm(false)
        setHasUnsavedChanges(false)
        //dispatch(setAddTicketFromCameras(false))
        //dispatch(setSecondsForTicket(0))
        //dispatch(setDialogView(''))
        //dispatch(setSelectedTicket(false))
    }, [dispatch])

    const handleAttemptClose = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowUnsavedConfirm(true)
            return
        }
        handleForceClose()
    }, [hasUnsavedChanges, handleForceClose])

    // Si es la vista de agregar ticket, usar BaseModal
    if (dialogView === STORE_HUB.ADD_TICKET) {
        return (
            <>
                <BaseModal
                    isOpen={viewTicket}
                    onClose={handleAttemptClose}
                    onRequestClose={handleAttemptClose}
                    title={modalTitle}
                    width={700}
                    footer={
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                        {canScheduleTickets && (
                            <label className={`flex items-center gap-1 px-3 py-1.5 rounded-full border cursor-pointer select-none w-full sm:w-auto justify-center transition-colors duration-150 ${effectiveShowScheduleForm ? 'bg-indigo-50 dark:bg-indigo-500/20 border-indigo-300 dark:border-indigo-500' : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600'}`}>
                                <span className={`flex items-center gap-1.5 text-sm font-medium ${effectiveShowScheduleForm ? 'text-indigo-600 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-200'}`}>
                                    <HiOutlineClock className="text-lg" />
                                    {t('schedule_ticket', { defaultValue: 'Programar' })}
                                </span>
                                <span style={{ transform: 'scale(0.75)', transformOrigin: 'center' }}>
                                    <Switcher
                                        checked={effectiveShowScheduleForm}
                                        disabled={isSubmitting}
                                        onChange={(checked) => setShowTicketScheduleForm(checked)}
                                    />
                                </span>
                            </label>
                        )}
                        <div className="flex flex-row w-full sm:w-auto sm:ml-auto gap-2">
                            <Button
                                variant="plain"
                                onClick={handleAttemptClose}
                                disabled={isSubmitting}
                                className="w-full sm:w-auto"
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                variant="solid"
                                type="submit"
                                form="add-ticket-form"
                                loading={isSubmitting}
                                disabled={isSubmitting}
                                className="w-full sm:w-auto"
                            >
                                <span className="flex items-center justify-center gap-2 w-full">
                                    {effectiveShowScheduleForm && !isSubmitting && <HiOutlineClock className="text-lg text-white" />}
                                    {isSubmitting ? createLoadingLabel : createLabel}
                                </span>
                            </Button>
                        </div>
                    </div>
                }
            >
                <Suspense fallback={<></>}>
                    <AddTicket
                        onSubmittingChange={setIsSubmitting}
                        showTicketScheduleForm={effectiveShowScheduleForm}
                        onDirtyChange={handleDirtyChange}
                        onResponsibleChange={setIsResponsibleSelected}
                    />
                </Suspense>
                </BaseModal>
                <ConfirmDialog
                    isOpen={showUnsavedConfirm}
                    onClose={() => setShowUnsavedConfirm(false)}
                    onRequestClose={() => setShowUnsavedConfirm(false)}
                    type="warning"
                    title={t('unsaved_changes_title', { defaultValue: 'Cambios sin guardar' })}
                    onCancel={() => setShowUnsavedConfirm(false)}
                    onConfirm={handleForceClose}
                    confirmButtonColor="red-600"
                    cancelText={t('continue_editing', { defaultValue: 'Seguir editando' })}
                    confirmText={t('discard_changes', { defaultValue: 'Descartar' })}
                >
                    <p>{t('unsaved_changes_message', { defaultValue: 'Si cierras ahora, tendrás que completar la tarea desde cero. ¿Deseas salir de todos modos?' })}</p>
                </ConfirmDialog>
            </>
        )
    }

    // Para otras vistas, usar el Dialog original
    return (
        <Dialog
            isOpen={viewTicket}
            onClose={handleForceClose}
            onRequestClose={handleForceClose}
            width={700}
            contentClassName="overflow-hidden !my-0"
            full={isMobileDevice ? true : false}
            // Centrado vertical + alto 80vh: el overlay pasa a flex y el
            // contenido se hace relativo (sin top fijo) para que quede al
            // centro con el mismo espacio arriba/abajo. Antes el Dialog usaba
            // height=680 fijo + my-16, por eso en pantallas altas (2K) quedaba
            // chico y pegado arriba. En mobile la prop `full` overridea esto a
            // 100dvh, asi que solo afecta a desktop.
            style={{
                overlay: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
                content: { inset: 'unset', position: 'relative', height: '80vh', maxHeight: '80vh' },
            }}
        >
            <Suspense
                fallback={
                    <div className="flex items-center justify-center h-full">
                        <Spinner size={40} />
                    </div>
                }
            >
                {dialogView === STORE_HUB.TICKET && (
                    <div className='mt-6 h-[calc(100%-1.5rem)] overflow-y-auto pr-1'>
                        {isNewInboxTenant() ? (
                            <TicketContent data={selectedTicket} inDialog />
                        ) : (
                            <LegacyTicketContent data={selectedTicket} inDialog />
                        )}
                    </div>
                )}
            </Suspense>
        </Dialog>
    )
}

export default TicketDialog
